import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { connect } from 'redux-bundler-react';

import _cloneDeep from 'lodash/cloneDeep';
import _startCase from 'lodash/startCase';

import * as turf from '@turf/turf';
import * as XLSX from 'xlsx';
import * as yup from 'yup';
import { mdiFolderUploadOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import { Accordion } from '@trussworks/react-uswds';
import { useMutation } from '@tanstack/react-query';

import ErrorSummary from '@components/error-summary/ErrorSummary';
import ExternalLink from '@components/external-link/ExternalLink';
import FileInput from '@components/file-input/FileInput';
import Icon from '@components/icon/Icon';
import Loader from '@components/loader/Loader';
import LinkButton from '@components/link/linkButton';
import ResourceMap from '@components/map/ResourceMap';
import Section from '@components/section/Section';
import StepHeader from '@components/step-header/StepHeader';
import Tooltip from '@components/tooltip/tooltip';
import { AR_PROP_COL_IDX, convertArTableInputDataToApiData } from '@components/table-input/tableInput.helpers.ars';

import statusModal from '@forms/components/modals/statusModal';
import { formatState, generateMapUseTipsAccordionList, saveRequest } from '@pages/Forms/input-forms/helpers';
import HGMCodeInfoTable from '@pages/Forms/input-forms/common/HGMCodeInfoTable';
import AquaticResourcesTable from '@pages/Forms/input-forms/common/AquaticResourcesTable';

import {
  ErrorMessages,
  FileTypes,
  RequestStatus,
  ResourceFiles,
  ResponseStatuses,
  stateNamesByCode,
} from '@utils/enums';
import { formatCoordStr } from '@utils/helpers';
import { setNumberValue } from '@utils/regex';
import { tError, tSuccess } from '@utils/toast-helpers';
import { handleFileProcessing } from '@utils/fileProcessing';

import { useLookupTablesQuery } from '@api/services/lookup/hooks';
import {
  useGetAquaticResources,
  useGetAquaticResourceGeometries,
  useGetAquaticResourceValidations,
  useGetAquaticResourceUsaceDistrictValidation,
} from '@api/services/aquatic-resources/hooks';
import { AquaticResourcesMutations } from '@api/services/aquatic-resources';
import { AorQueries } from '@api/services/aor/query';
import { useGetGeometryDataMutation } from '@hooks';
import { useAquaticResources, useModal, useRequest, useTableInput } from '@stores';

import {
  AR_COLUMN_INDEX,
  AR_COLUMN_PROPERTY,
  cleanValue,
  columnToPropertyMap,
  createAquaticResourcesConfig,
  generateMultiPointGeoJson,
  getAquaticResourceValidationIds,
  getDefaultGeometry,
  getOptionsByKey,
  getRegulationTypesByState,
  getValidationSchema,
  mapImportedRowToProperties,
  mapToMeasurementUnit,
  propertyMapping,
  readFile,
  SortMap,
  TableWarnings,
} from '../helpers.ar';
import { saveArChanges, saveArDeletes } from '@src/api/services/aquatic-resources/helpers';

const AR_UPLOAD_LIMIT = 3000;
const AR_UPLOAD_SAVE_LIMIT = 1000;

import { ModalContent, ModalFooter } from '@components/modal';

const DeleteConfirmModal = ({ onDelete }) => (
  <ModalContent title='Confirm Deletion'>
    <section className='modal-body'>
      <p>Are you sure you want to delete the selected aquatic resources? This action cannot be undone.</p>
    </section>
    <ModalFooter onSave={() => onDelete()} saveText='Yes' cancelText='No' onCancel={() => {}} />
  </ModalContent>
);

export const AquaticResources = ({
  config,
  doDownloadFile,
  requestAPIData,
  requestFormData,
  requestFormDataLocation,
  stepNo,
}) => {
  const location = requestFormData?.location;
  const districtName = location?.district?.replace(/ District$/i, '') || '';
  const isJd = config.isJd;
  const isPermit = config.isPermit;
  const projectState = location?.state ? formatState(location?.state) : '';
  const schema = yup.object().shape({});

  const request = isJd && requestFormData?.request?.jdRequests?.[0];
  const requestId = requestFormData?.request?.requestID;
  const version = requestFormData?.request?.version;
  const requestStatus = requestFormData?.request?.requestStatus;
  const isDraft = requestStatus === RequestStatus.Draft;

  const determination = request?.jurisdictionalDetermination;
  const arTableId = `aquatic-resources-table-${requestId}`;

  const tableDataRef = useRef();

  const pendingDistrictValidationRef = useRef(0);
  const hasUnvalidatedPersistedUsaceDistrictMismatchRef = useRef(false);
  const wasUsaceDistrictValidationRefetchingRef = useRef(false);

  const [isTableInvalid, setIsTableInvalid] = useState(isPermit ? true : false);
  const [hasPendingUsaceDistrictMismatch, setHasPendingUsaceDistrictMismatch] = useState(false);
  const [stagedFile, setStagedFile] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const methods = useForm({ resolver: yupResolver(schema), mode: 'onBlur' });

  const {
    addTableInputRow,
    getTableInputAddedRowCount,
    getTableInputBlankRow,
    getTableInputData,
    getTableInputDeletes,
    getTableInputItemsPerPage,
    getTableInputPageData,
    getTableInputPageNumber,
    getTableInputRowCount,
    getTableInputSortColumn,
    getTableInputSortDirection,
    getTableInputUpdates,
    hasTableInputChanges,
  } = useTableInput();

  const { getAquaticResourceGeometries, removeAquaticResourceGeometry, setAquaticResourceGeometry } =
    useAquaticResources();

  const { mutateAsync: getGeometryDataAsync } = useGetGeometryDataMutation();
  const { openModal, openSecondaryModal } = useModal();
  const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched, arCsvData, setArCsvData } = useRequest();
  const { data: lookupTableData = {} } = useLookupTablesQuery();

  const savedArGeometryData = useGetAquaticResourceGeometries(requestId);
  const arGeometries = getAquaticResourceGeometries();

  const aquaticResourceStateCoordinateMismatchIds = useAquaticResources((state) => state.aquaticResourceMismatchIds);
  const geometryBoundaryInvalidPages = useAquaticResources(
    (state) => state.aquaticResourceGeometryBoundaryInvalidPages
  );
  const geometryBoundaryFailureIds = useAquaticResources((state) => state.aquaticResourceGeometryBoundaryFailureIds);
  const hasGeometryBoundaryFailure = geometryBoundaryFailureIds.length > 0;
  const setAquaticResourceMismatches = useAquaticResources((state) => state.setAquaticResourceMismatches);
  const setAquaticResourceDuplicateWaterNames = useAquaticResources(
    (state) => state.setAquaticResourceDuplicateWaterNames
  );
  const setAquaticResourceJdTypeMismatchIds = useAquaticResources((state) => state.setAquaticResourceJdTypeMismatchIds);
  const setAquaticResourceJdTypes = useAquaticResources((state) => state.setAquaticResourceJdTypes);
  const addAquaticResourceMismatches = useAquaticResources((state) => state.addAquaticResourceMismatches);
  const removeAquaticResourceMismatch = useAquaticResources((state) => state.removeAquaticResourceMismatch);
  const clearAquaticResourceGeometryBoundaryValidation = useAquaticResources(
    (state) => state.clearAquaticResourceGeometryBoundaryValidation
  );

  const tableData = getTableInputData(arTableId)?.pagedData;

  const tableDeletes = getTableInputDeletes(arTableId);
  const tableItemsPerPage = getTableInputItemsPerPage(arTableId) || 10;
  const tablePageNumber = getTableInputPageNumber(arTableId);
  const tablePageData = getTableInputPageData(arTableId, tablePageNumber);
  const tableRowCount = getTableInputRowCount(arTableId);
  const tableSortColumn = getTableInputSortColumn(arTableId);
  const tableSortDirection = getTableInputSortDirection(arTableId);
  const tableUpdates = getTableInputUpdates(arTableId);
  const tableAddedRowCount = getTableInputAddedRowCount(arTableId);

  const activeTableRowCount = Math.max(tableRowCount - Object.keys(tableDeletes).length, 0);

  const queryParams = useMemo(
    () => ({
      column: SortMap[tableSortColumn.trim()],
      dir: tableSortDirection,
      page: tablePageNumber,
      pageSize: tableItemsPerPage,
    }),
    [tableItemsPerPage, tablePageNumber, tableSortColumn, tableSortDirection]
  );

  const {
    items: aquaticResourcesItems,
    isLoading: isAquaticResourcesLoading,
    totalCount: aquaticResourcesTotalCount,
  } = useGetAquaticResources(queryParams, requestId);
  const { invalidPages, aquaticResourceValidations, jdTypes, validationUpdatedAt, showValidationLoader } =
    useGetAquaticResourceValidations(queryParams, requestId, isDraft);
  const isArEditingDisabled = isReadOnly || showValidationLoader;
  const { data: usaceDistrictValidation, isRefetching: isUsaceDistrictValidationRefetching } =
    useGetAquaticResourceUsaceDistrictValidation(requestId, version);

  const allInvalidPages = [...new Set([...(invalidPages ?? []), ...(geometryBoundaryInvalidPages ?? [])])];
  const { mismatchIDs: persistedMismatchIDs, duplicateWaterNameIds: persistedDuplicateWaterNameIds } = useMemo(
    () => getAquaticResourceValidationIds(aquaticResourceValidations),
    [aquaticResourceValidations]
  );

  const unsavedArData = useMemo(
    () =>
      Object.values(tableData || {})
        .flatMap((page) => page.rowData)
        ?.filter((row) => row[AR_COLUMN_INDEX.waterName]?.recordId?.toString().startsWith('new-record-')),
    [tableData]
  );
  const nullGeometryLabels = useMemo(
    () =>
      (aquaticResourcesItems ?? [])
        .filter(
          (resource) => resource?.aquaticResourceID != null && arGeometries?.[resource?.aquaticResourceID] == null
        )
        .map((resource) => resource?.waterName || `aquatic resource ${resource?.aquaticResourceID}`)
        .filter(Boolean),
    [aquaticResourcesItems, arGeometries]
  );

  const cowardinOptions = useMemo(
    () => getOptionsByKey(lookupTableData.Cowardins, 'shortCode'),
    [lookupTableData.Cowardins]
  );

  const hgmCodeOptions = useMemo(
    () => getOptionsByKey(lookupTableData.HGMCodes, 'shortCode'),
    [lookupTableData.HGMCodes]
  );

  const stateOptions = useMemo(
    () => getOptionsByKey(lookupTableData.StateRegulations, 'state'),
    [lookupTableData.StateRegulations]
  );

  const regulationTypesByState = useMemo(
    () => getRegulationTypesByState(lookupTableData.ARTypes, lookupTableData.StateRegulations),
    [lookupTableData.ARTypes, lookupTableData.StateRegulations]
  );

  const resourceData = useMemo(() => {
    const mapData = { type: 'FeatureCollection', features: [] };

    // generate saved AR map features
    savedArGeometryData?.forEach((geometryData) => {
      const recordId = geometryData?.aquaticResourceID;
      const geometry = arGeometries?.[recordId] || geometryData?.geometry;
      const rowUpdates = tableUpdates?.[recordId] || {};
      const waterName = rowUpdates[AR_COLUMN_INDEX.waterName] || geometryData?.waterName;

      mapData.features.push({
        type: 'Feature',
        properties: { waterName },
        geometry,
      });
    });

    // generate unsaved AR map features
    unsavedArData?.forEach((newArRecord) => {
      const recordId = newArRecord[0].recordId;
      const geometry = arGeometries[recordId];
      const rowUpdates = tableUpdates?.[recordId] || {};
      const waterName = rowUpdates[AR_COLUMN_INDEX.waterName] || newArRecord[AR_COLUMN_INDEX.waterName]?.value;

      const feature = {
        type: 'Feature',
        properties: { waterName },
        geometry,
      };
      mapData.features.push(feature);
    });

    return mapData;
  }, [arGeometries, savedArGeometryData, unsavedArData, tableUpdates]);

  useEffect(() => {
    savedArGeometryData?.forEach((geometryData) => {
      const recordId = geometryData?.aquaticResourceID;
      const geometry = geometryData?.geometry;

      if (recordId != null && geometry && !arGeometries?.[recordId]) {
        setAquaticResourceGeometry(recordId, geometry);
      }
    });
  }, [arGeometries, savedArGeometryData, setAquaticResourceGeometry]);

  const hasStateCoordinateMismatch = aquaticResourceStateCoordinateMismatchIds.some(
    (aquaticResourceId) => !tableDeletes?.[aquaticResourceId]
  );
  const isTableEmpty = activeTableRowCount === 0;
  const hasNullGeometry = nullGeometryLabels.length > 0;
  const isValid =
    !hasStateCoordinateMismatch && !hasGeometryBoundaryFailure && !isTableInvalid && !isTableEmpty && !hasNullGeometry;
  const isTouched = isSectionTouched(stepNo);

  const hasPersistedUsaceDistrictMismatch = usaceDistrictValidation?.status === ResponseStatuses.ValidationFailed;
  const arTableWarningMessages = useMemo(() => {
    if (!hasPersistedUsaceDistrictMismatch && !hasPendingUsaceDistrictMismatch) {
      return [];
    }
    return [
      "The table includes Aquatic Resource(s) located outside the project's assigned USACE District. This is only a warning and will NOT prevent submission of this request.",
    ];
  }, [hasPendingUsaceDistrictMismatch, hasPersistedUsaceDistrictMismatch]);

  const validationSchema = useMemo(() => {
    const baseSchema = getValidationSchema({
      cowardinOptions,
      features: resourceData.features,
      hgmCodeOptions,
      location,
      regulationTypesByState,
      stateOptions,
    });

    return config.buildValidationSchema(baseSchema, {
      determination,
    });
  }, [
    config,
    cowardinOptions,
    determination,
    hgmCodeOptions,
    location,
    regulationTypesByState,
    resourceData.features,
    stateOptions,
  ]);

  const determineRegulation = useCallback(
    (state) => {
      if (!state) return null;
      const regulationIndex = lookupTableData?.StateRegulations?.findIndex(
        (stateReg) => stateReg?.state?.toUpperCase() === state?.toUpperCase()
      );
      const regulation = lookupTableData?.StateRegulations?.[regulationIndex]?.regulationType;
      return regulation;
    },
    [lookupTableData?.StateRegulations]
  );

  const normalizeIncomingProperties = useCallback(
    (props = {}) => {
      const stateValue = props.state || projectState;
      const state = stateValue ? formatState(_startCase(String(stateValue).toLowerCase())) : null;
      const jurisdictionalDeterminationType = props?.jurisdictionalDeterminationType
        ? config.formatJurisdictionalTypeString(props.jurisdictionalDeterminationType)
        : config.getDefaultJurisdictionalType({ jdType: determination, determination });
      const measurementAmount =
        props?.measurementAmount !== undefined &&
        props?.measurementAmount !== null &&
        String(props?.measurementAmount).trim() !== ''
          ? Number(props.measurementAmount)
          : null;

      return {
        waterName: props.waterName,
        state,
        latitude: formatCoordStr(props?.latitude ?? requestFormDataLocation?.latitude),
        longitude: formatCoordStr(props?.longitude ?? requestFormDataLocation?.longitude),
        measurementType: props?.measurementType ? _startCase(String(props.measurementType).toLowerCase()) : null,
        measurementAmount,
        measurementUnit: props?.measurementUnit ? mapToMeasurementUnit(props.measurementUnit) : null,
        localWaterway: props?.localWaterway || null,
        cowardinCode: props.cowardinCode?.toUpperCase?.() || null,
        hgmCode: props.hgmCode?.toUpperCase?.() || null,
        aquaticResourceType: props.aquaticResourceType?.toUpperCase?.() || null,
        jurisdictionalDeterminationType,
        regulationType: determineRegulation(state),
      };
    },
    [config, determination, determineRegulation, projectState, requestFormDataLocation]
  );

  const getBlankRow = useCallback(
    ({ properties: incomingProps, type } = { properties: {}, type: 'Point' }) => {
      const blankRow = getTableInputBlankRow(arTableId);
      const properties = normalizeIncomingProperties(incomingProps);
      const newFeatureName = `Resource ${tableRowCount + 1} - ${type}`;

      blankRow[AR_COLUMN_INDEX.waterName].value = properties.waterName?.trim?.() || newFeatureName;
      blankRow[AR_COLUMN_INDEX.state].value = properties.state || requestFormDataLocation?.state || '';
      blankRow[AR_COLUMN_INDEX.latitude].value = properties.latitude || '';
      blankRow[AR_COLUMN_INDEX.longitude].value = properties.longitude || '';
      blankRow[AR_COLUMN_INDEX.measurementType].value = properties.measurementType;
      blankRow[AR_COLUMN_INDEX.measurementAmount].value = properties.measurementAmount;
      blankRow[AR_COLUMN_INDEX.measurementUnit].value = properties.measurementUnit;
      blankRow[AR_COLUMN_INDEX.localWaterway].value = properties.localWaterway;
      blankRow[AR_COLUMN_INDEX.cowardinCode].value = properties.cowardinCode;
      blankRow[AR_COLUMN_INDEX.hgmCode].value = properties.hgmCode;
      blankRow[AR_COLUMN_INDEX.jdType].value = properties.jurisdictionalDeterminationType;
      blankRow[AR_COLUMN_INDEX.arType].value = properties.aquaticResourceType;

      blankRow[AR_PROP_COL_IDX.regulationType] = {
        ...blankRow[AR_COLUMN_INDEX.waterName],
        columnId: AR_PROP_COL_IDX.regulationType,
        typeId: AR_PROP_COL_IDX.regulationType,
        value: properties.regulationType,
      };

      return blankRow;
    },
    [arTableId, getTableInputBlankRow, normalizeIncomingProperties, requestFormDataLocation, tableRowCount]
  );

  const addFeature = useCallback(
    async (properties, geometry) => {
      if (properties.latitude && properties.longitude && !properties.state) {
        const districtLocations = await AorQueries.getDistrictLocationFromGeometry(geometry);
        const stateCode = districtLocations?.[0]?.stateCode || '';

        if (stateCode) {
          properties.state = formatState(stateCode);
        }
      }

      const type = geometry?.type || 'Point';
      const newRow = getBlankRow({ properties, type });
      const arId = newRow[0].recordId;

      setAquaticResourceGeometry(arId, geometry);
      addTableInputRow(arTableId, newRow);
    },
    [addTableInputRow, arTableId, getBlankRow, setAquaticResourceGeometry]
  );

  const validatePendingUsaceDistrict = useCallback(async () => {
    if (requestId == null || version == null || !districtName) {
      return;
    }
    const changes = convertArTableInputDataToApiData(requestId, version);
    const resourcesToValidate = [...changes.addedArs, ...changes.updatedArs];
    const coordinates = resourcesToValidate
      .filter(
        (resource) =>
          resource.latitude != null &&
          resource.longitude != null &&
          !isNaN(Number(resource.latitude)) &&
          !isNaN(Number(resource.longitude))
      )
      .map((resource) => [Number(resource.longitude), Number(resource.latitude)]);
    if (coordinates.length === 0) {
      setHasPendingUsaceDistrictMismatch(hasUnvalidatedPersistedUsaceDistrictMismatchRef.current);
      return;
    }
    const validationId = ++pendingDistrictValidationRef.current;
    try {
      const districtLocations = await AorQueries.getDistrictLocationFromGeometry({
        type: 'MultiPoint',
        coordinates,
      });
      if (validationId !== pendingDistrictValidationRef.current) {
        return;
      }
      const flattenedDistricts = districtLocations.map((location) => location.district);
      const hasMismatch = flattenedDistricts.some(
        (district) =>
          district
            ?.replace(/\s+District\s*$/i, '')
            .trim()
            .toLowerCase() !== districtName.toLowerCase()
      );
      setHasPendingUsaceDistrictMismatch(hasUnvalidatedPersistedUsaceDistrictMismatchRef.current || hasMismatch);
    } catch (error) {
      console.error('Failed to validate pending aquatic resource USACE districts:', error);
    }
  }, [districtName, requestId, version]);

  const handleMapAddFeature = useCallback(
    (optionalProperties, optionalGeometry) => {
      addFeature(optionalProperties, optionalGeometry);
      void validatePendingUsaceDistrict();
    },
    [addFeature, validatePendingUsaceDistrict]
  );

  const normalizeImportedProperties = useCallback(
    (properties = {}) => {
      const jdTypeFromInput = properties?.jurisdictionalDeterminationType
        ? config.formatJurisdictionalTypeString(String(properties.jurisdictionalDeterminationType))
        : null;

      const jdTypeFallback = config.isJd ? determination || null : null;

      return {
        ...properties,
        measurementType: properties?.measurementType ? _startCase(String(properties.measurementType)) : null,
        measurementUnit: properties?.measurementUnit ? mapToMeasurementUnit(String(properties.measurementUnit)) : null,
        measurementAmount:
          properties?.measurementAmount !== undefined &&
          properties?.measurementAmount !== null &&
          String(properties.measurementAmount).trim() !== ''
            ? Number(properties.measurementAmount)
            : null,
        cowardinCode: properties?.cowardinCode ? String(properties.cowardinCode).toUpperCase() : null,
        hgmCode: properties?.hgmCode ? String(properties.hgmCode).toUpperCase() : null,
        aquaticResourceType: properties?.aquaticResourceType
          ? String(properties.aquaticResourceType).toUpperCase()
          : null,
        jurisdictionalDeterminationType: jdTypeFromInput || jdTypeFallback,
      };
    },
    [config, determination]
  );

  const addFeaturesFromRows = useCallback(
    (rows = []) => {
      if (!rows.length) return;

      rows.forEach((row) => {
        const arProperties = normalizeImportedProperties(mapImportedRowToProperties(row, columnToPropertyMap));
        const geometry = getDefaultGeometry(arProperties.latitude, arProperties.longitude);
        addFeature(arProperties, geometry);
      });
    },
    [addFeature, normalizeImportedProperties]
  );

  const validateFileData = useCallback(
    async (rowData) => {
      const providedHeaders = [...new Set(rowData.flatMap((data) => Object.keys(data).map((h) => h.trim())))];
      const expectedHeaders = Object.keys(columnToPropertyMap);
      const headersMatch = providedHeaders.every((header) => expectedHeaders.includes(header));

      const numbersValid = rowData.map((feature) => {
        const latitude = cleanValue(feature?.Latitude);
        const longitude = cleanValue(feature?.Longitude);
        const measurementAmount = cleanValue(feature?.['Measurement Amount']);
        const isValidCoordinate =
          !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude)) && !isNaN(parseFloat(measurementAmount));
        return isValidCoordinate;
      });

      const isValidNumbers = !numbersValid.includes(false);

      if (!headersMatch) {
        openSecondaryModal(statusModal, { msg: ErrorMessages.InvalidHeaders });
        setStagedFile([]);
        setFileInputKey(Date.now());
        return false;
      }

      if (!isValidNumbers) {
        openSecondaryModal(statusModal, { msg: ErrorMessages.InvalidCoordinates });
        setStagedFile([]);
        setFileInputKey(Date.now());
        return false;
      }

      const isValidDistrict = await AorQueries.isGeometryInValidDistrict(generateMultiPointGeoJson(rowData));

      if (isValidDistrict === false) {
        openSecondaryModal(statusModal, {
          msg: ErrorMessages.InvalidDistrictCsv,
          status: 'CSV contains invalid coordinates',
        });
        setStagedFile([]);
        setFileInputKey(Date.now());
        return false;
      }

      return true;
    },
    [openSecondaryModal]
  );

  const processFilesFromCsv = useCallback(
    async (fileNames) => {
      if (!fileNames || fileNames.length === 0) return;

      const toastId = toast.loading('Processing file(s)...');

      try {
        const allRowData = [];

        for (const file of fileNames) {
          const fileContents = await readFile(file);
          const workbook = XLSX.read(fileContents, { type: 'buffer' });

          workbook.SheetNames.forEach((sheetName) => {
            const sheet = workbook.Sheets[sheetName];

            for (const cell in sheet) {
              if (cell[0] === '!') continue;
              if (sheet?.[cell]?.v !== undefined && sheet?.[cell]?.v !== null) {
                sheet[cell].v = cleanValue(sheet[cell].v);
                sheet[cell].t = 's';
              }
            }

            const rowObject = XLSX.utils.sheet_to_json(sheet);
            allRowData.push({ sheetName, rowObject });
          });
        }

        const flattenedData = allRowData.flatMap((entry) => entry.rowObject);
        const uploadLimit = AR_UPLOAD_LIMIT > tableAddedRowCount ? AR_UPLOAD_LIMIT - tableAddedRowCount : 0;
        const truncatedData = flattenedData.slice(0, uploadLimit);
        const totalNumUploads = truncatedData.length + tableAddedRowCount;
        const isValid = truncatedData.length ? await validateFileData(truncatedData) : true;

        if (isValid) {
          addFeaturesFromRows(truncatedData);
          void validatePendingUsaceDistrict();

          if (flattenedData.length > uploadLimit) {
            tError(
              toastId,
              `No more than ${AR_UPLOAD_LIMIT.toLocaleString()} new aquatic resources records may be added at once. The first ${AR_UPLOAD_LIMIT.toLocaleString()} rows have been added to your aquatic resources. Divide your CSV data accordingly.`,
              { autoClose: false }
            );
          } else {
            if (totalNumUploads >= AR_UPLOAD_SAVE_LIMIT) {
              saveRequest();
            }

            tSuccess(toastId, 'File(s) processed successfully.');
          }
        } else {
          tError(toastId, 'File validation failed.');
        }
      } catch (error) {
        console.error(error);
        tError(toastId, 'An error occurred while processing file(s).');
      }
    },
    [addFeaturesFromRows, tableAddedRowCount, validateFileData, validatePendingUsaceDistrict]
  );

  const processGdbData = useCallback(
    async (files) => {
      const gdbFile = files?.[0];
      if (!gdbFile) return;

      const result = await getGeometryDataAsync({ gdbFile, firstPolygonOnly: false });

      if (result.success !== true) {
        const toastId = toast.loading('error');
        tError(toastId, result.message ?? 'Failed to process GDB file.');
        return;
      }

      const correctedGeoJson = turf.flip(result.data);

      correctedGeoJson.features.forEach((feature, index) => {
        const coordinates = feature?.geometry?.coordinates;
        let centroidCoordinates;

        const normalizedProperties = Object.keys(feature.properties).reduce((acc, key) => {
          const lowerCaseKey = key.toLowerCase();
          if (lowerCaseKey in propertyMapping) {
            acc[propertyMapping[lowerCaseKey]] = feature.properties[key];
          }
          return acc;
        }, {});

        switch (feature?.geometry?.type?.toUpperCase()) {
          case 'MULTIPOINT': {
            const geoJSONMultiPoint = turf.multiPoint(coordinates);
            const centroid = turf.centerOfMass(geoJSONMultiPoint);
            centroidCoordinates = centroid.geometry.coordinates;
            break;
          }
          case 'POINT': {
            const geoJSONPoint = turf.point(coordinates);
            const centroid = turf.centroid(geoJSONPoint);
            centroidCoordinates = centroid.geometry.coordinates;
            break;
          }
          case 'MULTILINESTRING': {
            const geoJSONMultiLineString = turf.multiLineString(coordinates);
            if (
              geoJSONMultiLineString.geometry.coordinates.length > 0 &&
              geoJSONMultiLineString.geometry.coordinates[0].length > 0
            ) {
              const centroid = turf.centerOfMass(geoJSONMultiLineString);
              centroidCoordinates = centroid.geometry.coordinates;
            } else {
              centroidCoordinates = null;
            }
            break;
          }
          default: {
            const multiPolygon = Array.isArray(coordinates[0][0][0]) ? coordinates : [coordinates];
            const geoJSONMultiPolygon = turf.multiPolygon(multiPolygon);
            const centroid = turf.centerOfMass(geoJSONMultiPolygon);

            centroidCoordinates = centroid.geometry.coordinates;
          }
        }

        const properties = {
          waterName: normalizedProperties?.waterName ?? `${feature.geometry.type}_${index}`,
          state: normalizedProperties?.state
            ? formatState(normalizedProperties.state)
            : (projectState ?? formatState(projectState)),
          latitude: centroidCoordinates ? formatCoordStr(centroidCoordinates[1]) : null,
          longitude: centroidCoordinates ? formatCoordStr(centroidCoordinates[0]) : null,
          measurementType: normalizedProperties?.measurementType
            ? _startCase(normalizedProperties.measurementType)
            : null,
          measurementUnit: normalizedProperties?.measurementUnit
            ? mapToMeasurementUnit(normalizedProperties.measurementUnit)
            : null,
          measurementAmount: normalizedProperties?.measurementAmount
            ? Number(normalizedProperties.measurementAmount)
            : null,
          hgmCode: normalizedProperties?.hgmCode ? normalizedProperties.hgmCode.toUpperCase() : null,
          localWaterway: normalizedProperties?.localWaterway ? normalizedProperties.localWaterway : null,
          cowardinCode: normalizedProperties?.cowardinCode ? normalizedProperties.cowardinCode.toUpperCase() : null,
          regulationType: normalizedProperties?.state
            ? determineRegulation(normalizedProperties.state)
            : determineRegulation(projectState),
          jurisdictionalDeterminationType: normalizedProperties?.jurisdictionalDeterminationType,
          aquaticResourceType: normalizedProperties?.aquaticResourceType
            ? normalizedProperties.aquaticResourceType.toUpperCase()
            : null,
        };
        const geometry = feature?.geometry;
        addFeature(properties, geometry);
      });

      if (!result?.success) {
        setStagedFile([]);
        setFileInputKey(Date.now());
      }
    },
    [addFeature, getGeometryDataAsync, projectState, determineRegulation]
  );

  const handleFileOnChange = useCallback(
    async (e) => {
      const filesArr = Array.from(e?.target?.files || []);
      if (filesArr.length === 0) return;

      await handleFileProcessing(async () => {
        setStagedFile(filesArr);

        try {
          const fileExtensions = filesArr.map((file) => {
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            return ext.trim();
          });

          if (fileExtensions.includes('zip') || fileExtensions.includes('gdb')) {
            await processGdbData(filesArr);
          } else {
            await processFilesFromCsv(filesArr);
          }
        } finally {
          setStagedFile([]);
          setFileInputKey(Date.now());
        }
      });
    },
    [processFilesFromCsv, processGdbData]
  );

  const { mutate: validateAquaticResourceMismatch } = useMutation({
    mutationFn: AquaticResourcesMutations.getAquaticResourceMismatch,
    onSuccess: (responseData, aquaticResourcePayload) => {
      const aquaticResourceID = aquaticResourcePayload?.[0]?.aquaticResourceID;

      if (aquaticResourceID == null) {
        return;
      }

      if (responseData === null) {
        removeAquaticResourceMismatch([aquaticResourceID]);
        return;
      }

      const mismatchIDs = responseData?.[0]?.details?.aquaticResourceMismatchIDs ?? [];

      if (mismatchIDs.includes(aquaticResourceID)) {
        addAquaticResourceMismatches([aquaticResourceID]);
      } else {
        removeAquaticResourceMismatch([aquaticResourceID]);
      }
    },
    onError: (error) => {
      console.error('Error validating aquatic resource mismatch:', error);
    },
  });

  const handleTableChange = useCallback(
    ({ action, colIndex, recordId, rowIndex = 0, value }) => {
      if (action === 'delete') {
        clearAquaticResourceGeometryBoundaryValidation();

        if (recordId.toString().includes('new-record-')) {
          removeAquaticResourceGeometry(recordId);
        }

        return;
      }

      if (action !== 'update') return;

      clearAquaticResourceGeometryBoundaryValidation();

      const row = _cloneDeep(tablePageData[rowIndex]);
      const updatedRow = _cloneDeep(row);
      updatedRow[colIndex].value = value;

      let geometry = arGeometries?.[recordId];
      const latitude = updatedRow[AR_COLUMN_INDEX.latitude].value;
      const longitude = updatedRow[AR_COLUMN_INDEX.longitude].value;
      const property = AR_COLUMN_PROPERTY[colIndex];

      const hasLatLng = Number.isFinite(latitude) && Number.isFinite(longitude);
      const hasStateCoordinateValues = updatedRow.state && hasLatLng;
      const isLatLngChange = ['latitude', 'longitude'].includes(property);
      const shouldValidateStateCoordinateMismatch =
        (isLatLngChange || property === 'state') && hasStateCoordinateValues;

      if (isLatLngChange) {
        void validatePendingUsaceDistrict();

        if (geometry.type === 'Point') {
          geometry = {
            type: 'Point',
            coordinates: [parseFloat(longitude), parseFloat(latitude)],
          };

          setAquaticResourceGeometry(recordId, geometry);
        }
      }

      if (shouldValidateStateCoordinateMismatch) {
        validateAquaticResourceMismatch([
          {
            aquaticResourceID: recordId,
            aquaticResourceType: updatedRow[AR_COLUMN_INDEX.arType]?.value?.toString() || null,
            cowardinCode: updatedRow[AR_COLUMN_INDEX.cowardinCode]?.value?.toString() || null,
            geometry,
            hgmCode: updatedRow[AR_COLUMN_INDEX.hgmCode]?.value?.toString() || null,
            jurisdictionalDeterminationType: updatedRow[AR_COLUMN_INDEX.jdType]?.value?.toString() || null,
            latitude,
            localWaterway: updatedRow[AR_COLUMN_INDEX.localWaterway]?.value?.toString() || null,
            longitude,
            measurementAmount: Number(updatedRow[AR_COLUMN_INDEX.measurementAmount]?.value),
            measurementType: String(updatedRow[AR_COLUMN_INDEX.measurementType]?.value),
            measurementUnit: String(updatedRow[AR_COLUMN_INDEX.measurementUnit]?.value),
            regulationType: String(updatedRow[AR_COLUMN_INDEX.regulationType]?.value),
            requestID: requestId,
            state: String(updatedRow[AR_COLUMN_INDEX.state]?.value),
            version,
            waterName: String(updatedRow[AR_COLUMN_INDEX.waterName]?.value),
          },
        ]);
      }
    },
    [
      arGeometries,
      clearAquaticResourceGeometryBoundaryValidation,
      removeAquaticResourceGeometry,
      requestId,
      setAquaticResourceGeometry,
      tablePageData,
      validateAquaticResourceMismatch,
      validatePendingUsaceDistrict,
      version,
    ]
  );

  const handleTableRemove = useCallback(() => {
    const arChanges = convertArTableInputDataToApiData(requestId, version);

    openModal(DeleteConfirmModal, {
      onDelete: () => saveArDeletes({ arChanges, requestId }),
    });
  }, [openModal, requestId, version]);

  // Keep the AR form validation in sync with the table's overall validation state
  const handleTableValidation = useCallback((isValid) => {
    setIsTableInvalid(!isValid);
  }, []);

  // build the validation errors displayed by the ErrorSummary without relying on react-hook-form state
  const tableErrors = useMemo(() => {
    if (isTableEmpty) {
      return {
        aquaticResources: {
          message: 'Aquatic Resources: At least one aquatic resource is required',
        },
      };
    }

    if (hasNullGeometry) {
      return {
        aquaticResources: {
          message: `Missing geometry for Aquatic Resource:\n${nullGeometryLabels.join('\n')}`,
        },
      };
    }

    if (isTableInvalid || hasStateCoordinateMismatch) {
      return {
        aquaticResources: {
          message: 'Aquatic Resources: Table contains invalid rows',
        },
      };
    }
    return {};
  }, [hasNullGeometry, hasStateCoordinateMismatch, isTableEmpty, isTableInvalid, nullGeometryLabels]);

  const handleBeforeTableSort = useCallback(async () => {
    if (requestId == null || version == null) {
      return false;
    }

    if (!hasTableInputChanges(arTableId)) {
      return true;
    }

    try {
      const changes = convertArTableInputDataToApiData(requestId, version);
      await saveArChanges(changes);
      if (hasPendingUsaceDistrictMismatch) {
        hasUnvalidatedPersistedUsaceDistrictMismatchRef.current = true;
      }
      return true;
    } catch (error) {
      console.error('Failed to save aquatic resources before sorting:', error);
      return false;
    }
  }, [arTableId, hasPendingUsaceDistrictMismatch, hasTableInputChanges, requestId, version]);

  useEffect(() => {
    const csvFiles = arCsvData ? [arCsvData] : [];
    if (csvFiles.length === 0) return;

    processFilesFromCsv(csvFiles).finally(() => {
      setArCsvData(null);
    });
  }, [arCsvData, processFilesFromCsv, setArCsvData]);

  useEffect(() => {
    const enrichment = config.updateSectionEnrichment(false, isValid, isReadOnly);
    updateSection(stepNo, { valid: enrichment.valid, ...(enrichment.label && { label: enrichment.label }) });
  }, [isValid, updateSection, stepNo, isReadOnly, config]);

  useEffect(() => {
    if (requestAPIData?.request?.aquaticResources) {
      const mappedResources = {
        type: 'FeatureCollection',
        features: requestAPIData.request.aquaticResources.map((resource) => {
          const { geometry, ...properties } = resource;
          const newProperties = {
            ...properties,
            latitude: formatCoordStr(properties?.latitude),
            longitude: formatCoordStr(properties?.longitude),
            measurementAmount: setNumberValue(properties?.measurementAmount, false),
          };
          return { type: 'Feature', properties: newProperties, geometry };
        }),
      };

      tableDataRef.current = mappedResources.features.map((feature) => ({
        ...feature.properties,
        geometry: feature.geometry,
      }));
    }
  }, [requestAPIData]);

  useEffect(() => {
    if (isUsaceDistrictValidationRefetching) {
      wasUsaceDistrictValidationRefetchingRef.current = true;
      return;
    }
    if (wasUsaceDistrictValidationRefetchingRef.current) {
      hasUnvalidatedPersistedUsaceDistrictMismatchRef.current = false;
      setHasPendingUsaceDistrictMismatch(false);
      wasUsaceDistrictValidationRefetchingRef.current = false;
    }
  }, [isUsaceDistrictValidationRefetching]);

  useEffect(() => {
    if (!validationUpdatedAt) {
      return;
    }

    const jdTypeMismatchIds = Object.entries(aquaticResourceValidations)
      .filter(([, validation]) => validation.hasJdTypeMismatch === true)
      .map(([id]) => Number(id));

    setAquaticResourceMismatches(persistedMismatchIDs);
    setAquaticResourceDuplicateWaterNames(persistedDuplicateWaterNameIds);
    setAquaticResourceJdTypeMismatchIds(jdTypeMismatchIds);
    setAquaticResourceJdTypes(jdTypes);
  }, [
    aquaticResourceValidations,
    jdTypes,
    persistedDuplicateWaterNameIds,
    persistedMismatchIDs,
    setAquaticResourceDuplicateWaterNames,
    setAquaticResourceMismatches,
    setAquaticResourceJdTypeMismatchIds,
    setAquaticResourceJdTypes,
    validationUpdatedAt,
  ]);

  // Add geometry for manually added records
  useEffect(() => {
    tablePageData.forEach((row) => {
      const arId = row?.[0]?.recordId || '';
      let geometry = arGeometries?.[arId];

      if (!geometry && arId.toString().includes('new-record-')) {
        const latitude = row?.[AR_COLUMN_INDEX.latitude]?.value;
        const longitude = row?.[AR_COLUMN_INDEX.longitude]?.value;
        geometry = getDefaultGeometry(latitude, longitude);

        setAquaticResourceGeometry(arId, geometry);
      }
    });
  }, [arGeometries, tablePageData, setAquaticResourceGeometry]);

  return (
    <FormProvider {...methods}>
      {(isTouched || hasStateCoordinateMismatch) && !isReadOnly && (
        <ErrorSummary errors={tableErrors} sectionNo={stepNo} />
      )}
      <StepHeader
        text='Aquatic Resource Inventory'
        subtext={
          <>
            Provide an inventory of all aquatic resources within the project site.{' '}
            <ExternalLink
              href='https://youtu.be/KuGsz-w0XPw'
              title='View this video demonstration of different ways to enter Aquatic Resources information into the RRS.'
              content='View this video demonstration of different ways to enter Aquatic Resources information into the RRS.'
              aria-label='View this video demonstration of different ways to enter Aquatic Resources information into the RRS.'
            />{' '}
            Aquatic resources can be provided in this section by adding them individually or bulk loading them using the
            provided bulk upload CSV or geodatabase (GDB) templates.{' '}
            {config.renderPermitExtras && (
              <Tooltip
                name='gDBTemplateTooltip'
                content='Aquatic resources are the resources within a project location upon which USACE would perform a jurisdictional determination and/or permit impacts or mitigation. There may be multiple aquatic resources within a project location. Please provide a list of aquatic resources where the impacts are occurring, or for permittee responsible mitigation, the compensatory mitigation site, their location, and dimensions. Aquatic resources include wetland waters (i.e., vernal pools, swales, freshwater marsh, other seasonal wetlands, etc.), non-wetland waters (i.e., ponds, lakes, streams, rivers, drainages, ditches, etc.), and riparian areas (i.e., mixed riparian scrub, mixed riparian woodland, etc.).'
              />
            )}
          </>
        }
      >
        <>
          <div className='margin-top-1'>
            Download PDF Bulk Upload Guide:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ARBulkUploadGuide, FileTypes.Resource)}
                  title='Aquatic Resource Bulk Upload Guide [PDF, 6 pages]'
                  content='Aquatic Resource Bulk Upload Guide [PDF, 6 pages]'
                />
              </li>
            </ul>
          </div>
          <div className='margin-top-1 margin-bottom-2'>
            Download Geodatabase Guide:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ARGeodatabaseQuickGuide, FileTypes.Resource)}
                  title='AR Geodatabase Quick Guide [PDF, 8 pages]'
                  content='AR Geodatabase Quick Guide [PDF, 8 pages]'
                />
              </li>
              <ul>
                <li>
                  This guide provides instructions on how to use the Aquatic Resource Bulk Upload GDB, including how to
                  edit and add data.
                </li>
              </ul>
            </ul>
          </div>
          <div className='margin-top-1 margin-bottom-2'>
            Download GDB or CSV Bulk Upload Template:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ARBulkUploadGDBTemplate, FileTypes.Resource)}
                  title='Aquatic Resource Bulk Upload GDB Template [ZIP, 71 KB]'
                  content='Aquatic Resource Bulk Upload GDB Template [ZIP, 71 KB]'
                />
              </li>
              <ul>
                <li>Use for uploading lines and polygons related to aquatic resources</li>
              </ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.ARBulkUploadCSV, FileTypes.Resource)}
                  title='Resource Bulk Upload CSV Template [CSV, 0.16 KB]'
                  content='Resource Bulk Upload CSV Template [CSV, 0.16 KB]'
                />
              </li>
              <ul>
                <li>Use for uploading centroids associated with aquatic resources</li>
              </ul>
            </ul>
          </div>
          <div className='padding-bottom-1 margin-bottom-1'>
            Use the "Add New Row" button to individually add aquatic resources to the inventory table. If you have many
            aquatic resources, you can use the drag and drop feature to add multiple aquatic resources at one time using
            the geodatabase or CSV bulk upload templates.
          </div>
          <div className='padding-bottom-1 margin-bottom-1'>
            ** For best results, provide latitude and longitude coordinates with at least six decimal places of
            accuracy. Coordinates with less precision may delay processing of your application.
          </div>
          <div className='padding-bottom-1 margin-bottom-1'>
            Use the <Icon aria-label='upload' path={mdiFolderUploadOutline} size='20px' /> icon or the drag and drop
            feature within the table below to upload a geodatabase. Geodatabases must be compressed (.zip).
          </div>
          <div className='padding-bottom-1 margin-bottom-1'>
            Geodatabase geometries must use a Coordinate Reference System(CRS) of WGS84.
          </div>
          <div className='padding-bottom-1 margin-bottom-2'>
            To delete one or multiple Aquatic Resources entered - Use the checkbox to the left of the "Waters
            Name/Label" box OR Click on the checkbox to the left of the "Add New Row" button to select multiple rows and
            then click on "Remove Selected Rows".
          </div>
          <Accordion
            bordered
            items={generateMapUseTipsAccordionList('resource')}
            className='margin-top-2 margin-bottom-1'
          />
        </>
      </StepHeader>
      <ResourceMap
        addFeature={handleMapAddFeature}
        onFileSelection={handleFileOnChange}
        requestFormData={requestFormData}
        resourceData={resourceData}
        stepNo={stepNo}
      />
      <Section label='Aquatic Resources'>
        {config.renderPermitExtras && (
          <>
            <Accordion
              bordered
              items={[
                {
                  title: 'Detailed HGM Code Information',
                  content: <HGMCodeInfoTable />,
                  id: '1',
                  headingLevel: 'h3',
                },
              ]}
            />
            <p>
              A complete list of Cowardin codes can be downloaded from&nbsp;
              <ExternalLink
                href='https://www.fws.gov/program/national-wetlands-inventory/classification-codes'
                title='U.S. Fish & Wildlife Service'
                content='U.S. Fish & Wildlife Service.'
              />
              .
            </p>
          </>
        )}
        <div id='aquaticResources'>
          <TableWarnings messages={arTableWarningMessages} />
          <FileInput
            key={fileInputKey}
            accept='.csv,.zip'
            arHeader={true}
            aria-label='Upload CSV or GDB (zipped) File'
            className='double-max-width-important'
            dragText='Drag .csv or .gdb (zipped) file here or '
            hint='Compressed GDB (.zip) files must be 5 MiB or smaller.'
            id='ar-bulk-file'
            name='ar-bulk-file'
            onChangeCallback={handleFileOnChange}
            value={stagedFile}
            readOnly={isArEditingDisabled}
          />
          {showValidationLoader && <Loader variant='section' loaderText='Validating aquatic resources...' />}

          <AquaticResourcesTable
            apiInvalidPageNumbers={allInvalidPages}
            apiValidationUpdatedAt={validationUpdatedAt}
            aquaticResourcesData={aquaticResourcesItems}
            determination={determination}
            getBlankRow={getBlankRow}
            onBeforeSort={handleBeforeTableSort}
            isDataLoading={isAquaticResourcesLoading}
            isReadOnly={isArEditingDisabled}
            onChange={handleTableChange}
            onRemove={handleTableRemove}
            onValidationChange={handleTableValidation}
            requestId={requestId}
            requestState={stateNamesByCode[requestFormData.location.state]}
            savedRowCount={aquaticResourcesTotalCount || 0}
            validationSchema={validationSchema}
          />
        </div>
      </Section>
    </FormProvider>
  );
};

const permitConfig = createAquaticResourcesConfig('permit');

export const PermitFormAquaticResources = connect(
  'doDownloadFile',
  'selectRequestFormData',
  'selectRequestAPIData',
  'selectRequestFormDataLocation',
  (props) => <AquaticResources {...props} config={permitConfig} />
);

export const PermitsFormAquaticResourcesMetadata = permitConfig.metadata;
PermitFormAquaticResources.metadata = permitConfig.metadata;

const jdConfig = createAquaticResourcesConfig('jd');

export const JDFormAquaticResources = connect(
  'doDownloadFile',
  'selectJDData',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectRequestFormDataLocation',
  (props) => <AquaticResources {...props} config={jdConfig} />
);

export const JDFormAquaticResourcesMetadata = jdConfig.metadata;
JDFormAquaticResources.metadata = jdConfig.metadata;
